import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  RULE_BUNDLE_PAYLOAD_MAX_BYTES,
  RULE_BUNDLE_SCHEMA_VERSION,
  adminRuleBundlePublishSchema,
  ruleBundlePayloadSchema,
  type AdminRuleBundlePublishInput,
  type RuleBundlePlatform,
  type RuleBundlePayload
} from "@shudian/contracts";
import {
  adminGetLatestRuleBundle,
  adminPublishRuleBundle
} from "../api/admin.js";
import { CalendarClock, FileJson2, PackageCheck, Send } from "lucide-react";
import { Field } from "../components/ui/Field.js";
import { Input } from "../components/ui/Input.js";
import { Button } from "../components/ui/Button.js";
import { Badge } from "../components/ui/Badge.js";
import { Alert } from "../components/ui/Alert.js";
import { FullPageSpinner } from "../components/ui/Spinner.js";
import { MetricCard, PageHeader, SectionHeader, SectionSurface } from "../components/PageScaffold.js";
import { CloudApiError, describeError } from "../lib/errors.js";
import { formatDateTime } from "../lib/format.js";

const termTextSchema = z.string().max(16 * 1024, "词表内容过长");
const RULE_BUNDLE_PLATFORM_OPTIONS: Array<{
  value: RuleBundlePlatform;
  label: string;
}> = [{ value: "ruten", label: "露天" }];

const publishFormSchema = z.object({
  bundleVersion: adminRuleBundlePublishSchema.shape.bundleVersion,
  expiresAt: adminRuleBundlePublishSchema.shape.expiresAt,
  maxTitleLength: z.coerce.number().int("标题长度必须是整数").min(1, "标题长度至少为 1").max(120, "标题长度不能超过 120"),
  requirePrice: z.boolean(),
  requireImage: z.boolean(),
  minImageCount: z.coerce.number().int("最少图片数必须是整数").min(0, "最少图片数不能小于 0").max(20, "最少图片数不能超过 20"),
  forbiddenWordsText: termTextSchema,
  sensitiveClaimsText: termTextSchema
});

type PublishFormValues = z.infer<typeof publishFormSchema>;

export function RuleBundlePage() {
  const queryClient = useQueryClient();
  const [selectedPlatform, setSelectedPlatform] = useState<RuleBundlePlatform>("ruten");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin", "rule-bundles", "latest", selectedPlatform],
    queryFn: () => adminGetLatestRuleBundle(selectedPlatform)
  });

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    watch,
    formState: { errors }
  } = useForm<PublishFormValues>({
    resolver: zodResolver(publishFormSchema),
    defaultValues: {
      bundleVersion: defaultBundleVersion(selectedPlatform),
      expiresAt: defaultExpiresAt(),
      maxTitleLength: 60,
      requirePrice: true,
      requireImage: true,
      minImageCount: 1,
      forbiddenWordsText: "replica\nfake\n仿品\n高仿",
      sensitiveClaimsText: "官方授权\n正品保证\n全新未拆"
    }
  });

  const updateSelectedPlatform = (nextPlatform: RuleBundlePlatform) => {
    const currentBundleVersion = getValues("bundleVersion").trim();
    const isGeneratedVersion =
      currentBundleVersion.length === 0 ||
      RULE_BUNDLE_PLATFORM_OPTIONS.some((option) => currentBundleVersion.startsWith(`rules-${option.value}-`));
    setSelectedPlatform(nextPlatform);
    if (isGeneratedVersion) {
      setValue("bundleVersion", defaultBundleVersion(nextPlatform), { shouldValidate: true });
    }
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  const watchedValues = watch();
  const payloadPreview = useMemo(() => {
    const payload = buildPayloadFromValues(watchedValues, selectedPlatform);
    return JSON.stringify(payload, null, 2);
  }, [selectedPlatform, watchedValues]);

  const mutation = useMutation({
    mutationFn: (input: AdminRuleBundlePublishInput) => adminPublishRuleBundle(input),
    onSuccess: async (published) => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "rule-bundles", "latest", published.platform] });
      await queryClient.invalidateQueries({ queryKey: ["admin", "overview"] });
      setSuccessMessage("规则包已发布并完成服务端 Ed25519 签名");
    },
    onError: (err) => {
      setErrorMessage(err instanceof CloudApiError ? describeError(err.code) : "发布失败");
    }
  });

  const onSubmit = handleSubmit((values) => {
    setErrorMessage(null);
    setSuccessMessage(null);
    let expiresAt: string;
    try {
      expiresAt = new Date(values.expiresAt).toISOString();
    } catch {
      setErrorMessage("规则包过期时间不合法");
      return;
    }
    const payload = buildPayloadFromValues(values, selectedPlatform);
    const parsedPayload = ruleBundlePayloadSchema.safeParse(payload);
    if (!parsedPayload.success) {
      setErrorMessage("规则包内容不合法，请检查必填项和数值范围");
      return;
    }
    if (new TextEncoder().encode(JSON.stringify(parsedPayload.data)).length > RULE_BUNDLE_PAYLOAD_MAX_BYTES) {
      setErrorMessage("规则包内容超过 256 KiB 上限");
      return;
    }
    mutation.mutate({
      bundleVersion: values.bundleVersion.trim(),
      expiresAt,
      payload: parsedPayload.data
    });
  });

  if (isLoading) return <FullPageSpinner />;
  if (isError) {
    const message = error instanceof CloudApiError ? describeError(error.code) : "加载失败";
    return <Alert tone="danger">{message}</Alert>;
  }

  return (
    <div>
      <PageHeader
        eyebrow="发布与采集 / 规则包"
        title="规则包"
        description="管理桌面端上架前校验规则。规则包由云端 API 签名后下发，桌面端验签后使用。"
        actions={<PlatformSelect value={selectedPlatform} onChange={updateSelectedPlatform} />}
      />

      {data && (
        <section className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-3">
          <MetricCard label="当前版本" value={data.bundleVersion ?? "未发布"} detail={formatRuleBundlePlatform(data.platform)} icon={<PackageCheck className="h-4 w-4" />} />
          <MetricCard
            label="发布状态"
            value={data.source === "published" ? "已发布" : "未发布"}
            detail={`发布人 ${data.publishedByAdminId ?? "未发布"}`}
            tone={data.source === "published" ? "success" : "warning"}
            icon={<Send className="h-4 w-4" />}
          />
          <MetricCard label="过期时间" value={formatDateTime(data.expiresAt)} detail={`发布时间 ${formatDateTime(data.publishedAt)}`} tone="warning" icon={<CalendarClock className="h-4 w-4" />} />
        </section>
      )}

      <SectionSurface className="mb-5">
        <SectionHeader title="当前平台规则包" description="桌面端店铺只拉取同平台规则包，并使用云端 API 公钥验签后才能使用。" />
        {data && (
          <dl className="grid grid-cols-1 gap-4 px-4 py-4 text-sm md:grid-cols-3">
            <Detail label="版本" value={data.bundleVersion ?? "未发布"} />
            <Detail label="平台" value={formatRuleBundlePlatform(data.platform)} />
            <Detail label="过期时间" value={formatDateTime(data.expiresAt)} />
            <Detail
              label="来源"
              value={
                <Badge tone={data.source === "published" ? "success" : "warning"}>
                  {data.source === "published" ? "已发布" : "未发布"}
                </Badge>
              }
            />
            <Detail label="发布时间" value={formatDateTime(data.publishedAt)} />
            <Detail label="发布人" value={data.publishedByAdminId ?? "未发布"} />
            <Detail label="公钥" value={<code className="font-mono text-xs">{data.publicKey}</code>} />
          </dl>
        )}
      </SectionSurface>

      <SectionSurface>
        <SectionHeader
          title="发布平台规则包"
          description={`${formatRuleBundlePlatform(selectedPlatform)}规则包由云端 API 服务端使用环境变量中的 Ed25519 私钥完成签名。`}
          actions={<FileJson2 className="h-4 w-4 text-text-muted" />}
        />
        <form className="space-y-5 px-4 py-4" onSubmit={onSubmit} noValidate>
          {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}
          {successMessage && <Alert tone="success">{successMessage}</Alert>}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="规则包版本" htmlFor="bundleVersion" required error={errors.bundleVersion?.message}>
              <Input id="bundleVersion" invalid={!!errors.bundleVersion} {...register("bundleVersion")} />
            </Field>
            <Field
              label="过期时间"
              htmlFor="expiresAt"
              required
              error={errors.expiresAt?.message}
              hint="ISO datetime，例如 2026-12-31T23:59:59Z"
            >
              <Input
                id="expiresAt"
                type="datetime-local"
                invalid={!!errors.expiresAt}
                {...register("expiresAt")}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Field label="标题最大长度" htmlFor="maxTitleLength" required error={errors.maxTitleLength?.message}>
              <Input
                id="maxTitleLength"
                type="number"
                min={1}
                max={120}
                invalid={!!errors.maxTitleLength}
                {...register("maxTitleLength")}
              />
            </Field>
            <Field label="最少图片数" htmlFor="minImageCount" required error={errors.minImageCount?.message}>
              <Input
                id="minImageCount"
                type="number"
                min={0}
                max={20}
                invalid={!!errors.minImageCount}
                {...register("minImageCount")}
              />
            </Field>
            <div className="grid grid-cols-1 gap-2 pt-5 text-sm text-text-primary">
              <label className="flex items-center gap-2">
                <input type="checkbox" className="h-4 w-4" {...register("requirePrice")} />
                必须有价格
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" className="h-4 w-4" {...register("requireImage")} />
                必须有图片
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field
              label="禁用词"
              error={errors.forbiddenWordsText?.message}
              hint="一行一个；命中后阻断发布确认。"
            >
              <textarea
                className="min-h-[160px] w-full rounded border border-surface-border bg-surface-panel px-2.5 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/70"
                {...register("forbiddenWordsText")}
              />
            </Field>
            <Field
              label="敏感声明"
              error={errors.sensitiveClaimsText?.message}
              hint="一行一个；命中后生成风险警告。"
            >
              <textarea
                className="min-h-[160px] w-full rounded border border-surface-border bg-surface-panel px-2.5 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/70"
                {...register("sensitiveClaimsText")}
              />
            </Field>
          </div>

          <Field label="规则包 JSON 预览" hint="预览内容只用于核对，发布时仍由结构化字段生成。">
            <pre className="max-h-[300px] overflow-auto rounded border border-surface-border bg-surface-panel px-3 py-2 font-mono text-xs text-text-primary">
              {payloadPreview}
            </pre>
          </Field>

          <Button type="submit" disabled={mutation.isPending}>
            <Send className="h-4 w-4" />
            {mutation.isPending ? "发布中..." : "发布规则包"}
          </Button>
        </form>
      </SectionSurface>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-text-muted">{label}</dt>
      <dd className="mt-0.5 text-sm text-text-primary">{value}</dd>
    </div>
  );
}

function PlatformSelect({
  value,
  onChange
}: {
  value: RuleBundlePlatform;
  onChange: (value: RuleBundlePlatform) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-xs font-medium text-text-secondary" htmlFor="ruleBundlePlatform">
      平台
      <select
        id="ruleBundlePlatform"
        className="h-9 min-w-[8rem] rounded border border-surface-border bg-surface-panel px-2.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/70"
        value={value}
        onChange={(event) => onChange(event.target.value as RuleBundlePlatform)}
      >
        {RULE_BUNDLE_PLATFORM_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function buildPayloadFromValues(values: Partial<PublishFormValues>, platform: RuleBundlePlatform): RuleBundlePayload {
  return {
    schemaVersion: RULE_BUNDLE_SCHEMA_VERSION,
    platform,
    listingChecks: {
      maxTitleLength: Number(values.maxTitleLength ?? 60),
      requirePrice: values.requirePrice ?? true,
      requireImage: values.requireImage ?? true,
      requireManualConfirmation: true
    },
    riskChecks: {
      forbiddenWords: parseTerms(values.forbiddenWordsText ?? ""),
      sensitiveClaims: parseTerms(values.sensitiveClaimsText ?? ""),
      minImageCount: Number(values.minImageCount ?? 1)
    }
  };
}

function formatRuleBundlePlatform(platform: RuleBundlePlatform) {
  return RULE_BUNDLE_PLATFORM_OPTIONS.find((option) => option.value === platform)?.label ?? platform;
}

function parseTerms(value: string): string[] {
  return value
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter((item, index, items) => item.length > 0 && items.indexOf(item) === index);
}

function defaultBundleVersion(platform: RuleBundlePlatform): string {
  const now = new Date();
  const yyyy = now.getUTCFullYear();
  const mm = String(now.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(now.getUTCDate()).padStart(2, "0");
  return `rules-${platform}-${yyyy}${mm}${dd}`;
}

function defaultExpiresAt(): string {
  const expires = new Date();
  expires.setUTCDate(expires.getUTCDate() + 30);
  expires.setUTCHours(0, 0, 0, 0);
  // datetime-local needs YYYY-MM-DDTHH:MM
  const iso = expires.toISOString();
  return iso.slice(0, 16);
}
